import { Button, Theme } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React from 'react'
import { useTranslation } from 'react-i18next'
import useLocalStorage from '../hooks/useLocalStorage'
import { PRIVACY_POLICY_FLATPAGE_URL } from '../urls'
import { Link } from 'react-router-dom'

const STORAGE_KEY = 'cookie_consent'

type TCookieConsent = 'accepted' | 'declined' | null

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1300,
    background: '#fff',
    borderTop: '1px solid #e0e0e0',
    boxShadow: '0 -2px 12px rgba(0, 0, 0, 0.08)',
    color: '#333',
  },
  inner: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 12,
    },
  },
  text: {
    flex: 1,
    margin: 0,
    fontSize: 16,
    lineHeight: 1.5,
  },
  link: {
    color: theme.palette.secondary.main,
    textDecoration: 'underline',
    '&:hover': { color: theme.palette.secondary.main },
  },
  actions: {
    display: 'flex',
    gap: 8,
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      '& > *': { flex: 1 },
    },
  },
}))

// Mirrors gtag(): GTM consent commands must be pushed as an `arguments` object, not a plain array
function gtag(..._args: unknown[]) {
  window.dataLayer = window.dataLayer || []
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments as unknown as Record<string, unknown>)
}

const pushConsent = (granted: boolean) => {
  const value = granted ? 'granted' : 'denied'
  gtag('consent', 'update', {
    analytics_storage: value,
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  })
  window.dataLayer.push({ event: 'cookie_consent_update', cookie_consent: granted ? 'accepted' : 'declined' })
}

const CookieConsentBanner: React.FC = () => {
  const { t } = useTranslation()
  const classes = useStyles()
  const [consent, setConsent] = useLocalStorage<TCookieConsent>(STORAGE_KEY, null)

  if (consent) return null

  const handleChoice = (granted: boolean) => {
    pushConsent(granted)
    setConsent(granted ? 'accepted' : 'declined')
  }

  return (
    <div className={classes.root} role='region' aria-label={t('cookieConsent.label', 'Cookie consent')}>
      <div className={classes.inner}>
        <p className={classes.text}>
          {t(
            'cookieConsent.text',
            'We use privacy-friendly analytics to understand how people use the site. Accept cookies to help us ' +
              'improve your experience with more detailed insights.'
          )}{' '}
          <Link className={classes.link} to={PRIVACY_POLICY_FLATPAGE_URL}>
            {t('cookieConsent.privacyPolicy', 'Privacy policy')}
          </Link>
        </p>
        <div className={classes.actions}>
          <Button variant='outlined' onClick={() => handleChoice(false)}>
            {t('cookieConsent.decline', 'No thanks')}
          </Button>
          <Button variant='contained' color='secondary' onClick={() => handleChoice(true)}>
            {t('cookieConsent.accept', 'Accept')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default CookieConsentBanner
